import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { CardContent, FormControl, List, ListItem, ListItemText, MenuItem, Select, Typography } from "@material-ui/core";
import { CardPageItem } from "../../custom/Page";
import { FormattedMessage } from "react-intl";
import { Roles } from "../../util";
import { country } from "./Terms";
import { Link, withRouter } from "react-router-dom";
import { ChevronRight } from "@material-ui/icons";
import ContractSearchField from "./ContractSearchField";

const ReportMenu = ({
  history,
  onlyMyClients = false,
  isContractManager = false,
  enableManagerReportSection = false,
  enableUsersReportSection,
  enableContractsByAgent,
  enableContractsByCustomer,
  enableContractsByHierarchy,
  enableContractsDirectBelow,
  enableContractsAllBelow,
}) => {
  const currentUser = useSelector((state) => state.user.user.me);
  const [taxResidence, setTaxResidence] = useState(currentUser.taxResidence);

  return (
    <>
      <CardPageItem raised>
        <CardContent>
          <ContractSearchField onNavigate={(id) => history.push(`/userContract/${id}`)} />
        </CardContent>
      </CardPageItem>

      {[Roles.Admin, Roles.FinanceManager].includes(currentUser.role) && (
        <CardPageItem raised>
          <CardContent>
            <Typography variant="h6">
              <FormattedMessage id="app.report.lccontracts" />
            </Typography>

            <>
              <Typography gutterBottom>
                <FormattedMessage id="app.report.lccontracts.selectTaxResidence" />
              </Typography>
              <FormControl
                variant="outlined"
                fullWidth
              >
                <Select
                  value={taxResidence}
                  onChange={(e) => setTaxResidence(e.target.value)}
                >
                  {country.map((ctry) => (
                    <MenuItem
                      key={ctry.value}
                      value={ctry.value}
                    >
                      <FormattedMessage id={ctry.input} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>

            <ReportContractMenuList taxResidence={taxResidence} />
          </CardContent>
        </CardPageItem>
      )}

      {(enableContractsByAgent ||
        enableContractsByCustomer ||
        enableContractsByHierarchy ||
        enableContractsDirectBelow ||
        enableContractsAllBelow) && (
        <CardPageItem raised>
          <CardContent>
            <Typography variant="h6">
              <FormattedMessage id="app.report.contract" />
            </Typography>
            <List>
              {enableContractsByAgent && (
                <ReportListItem
                  captionId="app.report.contract.byAgent"
                  path="/reports/contract/by-agent"
                />
              )}
              {enableContractsByHierarchy && (
                <ReportListItem
                  captionId="app.report.contract.byCustomer"
                  path="/reports/contract/by-team-hierarchy"
                />
              )}
              {enableContractsByCustomer && (
                <ReportListItem
                  captionId="app.report.contract.byCustomer"
                  path="/reports/contract/by-customer"
                />
              )}
              {enableContractsDirectBelow && (
                <ReportListItem
                  captionId="app.report.contract.directBelow"
                  path="/reports/contract/"
                />
              )}
              {enableContractsAllBelow && (
                <ReportListItem
                  captionId="app.report.contract.allBelow"
                  path="/reports/contract/all-below"
                />
              )}
            </List>
          </CardContent>
        </CardPageItem>
      )}

      {enableUsersReportSection && (
        <CardPageItem raised>
          <CardContent>
            <Typography variant="h6">
              <FormattedMessage id="app.user.report" />
            </Typography>
            <ReportListItem
              captionId="app.report.customer"
              path="/reports/users"
            />
            <ReportListItem
              captionId="app.report.byGroup"
              path="/reports/users/by-group"
            />
          </CardContent>
        </CardPageItem>
      )}
      {enableManagerReportSection && (
        <CardPageItem raised>
          <CardContent>
            <Typography variant="h6">
              <FormattedMessage id="app.report.manager" />
            </Typography>
            <ManagerMenuList user={currentUser} />
          </CardContent>
        </CardPageItem>
      )}
    </>
  );
};

const ReportListItem = ({ captionId, path, taxResidence, needTaxParam = true, onlyMyClients, isContractManager, enableHierarchyFilter }) => {
  const currentUser = useSelector((state) => state.user.user.me);
  const shouldAddTaxParam = needTaxParam && [Roles.Admin, Roles.FinanceManager].includes(currentUser.role);
  const to = {
    pathname: path,
    state: { onlyMyClients, isContractManager, enableHierarchyFilter },
    ...(shouldAddTaxParam ? { search: `?taxResidence=${encodeURIComponent(taxResidence)}` } : {}),
  };
  return (
    <ListItem
      button
      component={Link}
      to={to}
    >
      <ListItemText primary={<FormattedMessage id={captionId} />} />
      <ChevronRight color="action" />
    </ListItem>
  );
};

const ReportContractMenuList = ({ taxResidence, onlyMyClients, isContractManager }) => {
  return (
    <List>
      <ReportListItem
        captionId="app.contracts.justopened"
        path="/contractReportJustOpened"
        taxResidence={taxResidence}
        onlyMyClients={onlyMyClients}
        isContractManager={isContractManager}
      />
      <ReportListItem
        captionId="app.contracts.reportwaitingvalidation"
        path="/contractReportWaitingValidation"
        taxResidence={taxResidence}
        onlyMyClients={onlyMyClients}
        isContractManager={isContractManager}
      />
      <ReportListItem
        captionId="app.lccontracts.active"
        path="/contractReport"
        taxResidence={taxResidence}
        onlyMyClients={onlyMyClients}
        isContractManager={isContractManager}
      />
      <ReportListItem
        captionId="app.contracts.terminated"
        path="/contractReportTerminated"
        taxResidence={taxResidence}
        onlyMyClients={onlyMyClients}
        isContractManager={isContractManager}
      />
      <ReportListItem
        captionId="app.contracts.redeemed"
        path="/contractReportRedeemed"
        taxResidence={taxResidence}
        onlyMyClients={onlyMyClients}
        isContractManager={isContractManager}
      />
    </List>
  );
};

const ManagerMenuList = ({ user }) => {
  const taxResidence = "Brasil";
  return (
    <List>
      <ReportListItem
        captionId="app.report.financial.view"
        path="/report/agents-financial"
        taxResidence={user.taxResidence}
        needTaxParam={false}
      />
      <ReportListItem
        captionId="app.report.hierarchical"
        path="/managerAndAgents"
        taxResidence={user.taxResidence}
        needTaxParam={false}
      />
    </List>
  );
};

ReportMenu.propTypes = {
  onlyMyClients: PropTypes.bool,
  isContractManager: PropTypes.bool,
  enableManagerReportSection: PropTypes.bool,
  enableUsersReportSection: PropTypes.bool,
  enableContractsByAgent: PropTypes.bool,
  enableContractsByCustomer: PropTypes.bool,
  enableContractsByHierarchy: PropTypes.bool,
};

ReportMenu.defaultProps = {
  onlyMyClients: false,
  isContractManager: false,
  enableManagerReportSection: false,
  enableUsersReportSection: false,
  enableContractsByAgent: false,
  enableContractsByCustomer: false,
  enableContractsByHierarchy: false,
};

export default withRouter(React.memo(ReportMenu));
